// =============================================================================
// io/sync/practice.ts — the "Fix it" gym's data layer. #59.
// -----------------------------------------------------------------------------
// Transcribed from the old client's `_practiceSessionToDb` / `_practiceTradeToDb`
// serializers, its `byTable.practice_*` load mapping (app/index.html:4344-4357)
// and `supabase/migrations/20260724000001_practice.sql`.
//
// ── ⚠ WHAT PRACTICE IS, IN ONE SENTENCE, BECAUSE IT HAS BEEN RE-DERIVED WRONG ──
//
// `docs/PRACTICE_PLAN.md`, which is authoritative and says so:
//
//   **Practice is Backtesting with a different question.** Backtesting asks
//   "does this setup have edge?"; Practice asks "can I execute?". The mechanic is
//   IDENTICAL — you select completed trades, it imports a COPY, and the copy is
//   what you grade.
//
// It follows that Practice **does not discover** sessions. It does not scan,
// group, auto-detect or infer. A session is ONE IMPORT of the trades you picked.
// And it **never removes, hides, flags or relocates** the original: a replay fill
// stays an ordinary trade, counting or not according to its ACCOUNT's
// exclude-from-metrics flag.
//
// 🔴 The plan's own warning: *"If you find yourself writing a trade-level
// `isReplay`-style flag that changes live-app behaviour, you have already
// deviated. Stop."*
//
// ── 🚨 A PRACTICE TRADE IS A WHOLE TRADE, AND THAT IS THE WHOLE DESIGN ──
//
// `practice_trades` stores the ENTIRE trade object in `extras`; the flat columns
// are a denormalised projection for querying. That is why the old client
// annotates a practice trade with the existing trade form and no new UI at all.
//
// 🔴 SO D62 APPLIES HARDER HERE THAN ANYWHERE. A typed model that covers only the
// columns would, on save, write back an `extras` blob missing every field it does
// not know about — mistake tags, discipline booleans, confluences, executions,
// recordings, the excursion series. That is not a partial update; it is a silent
// delete of the annotation the user came here to make.
//
// The rule this module holds: **the blob is carried, never rebuilt.** A save
// spreads the ORIGINAL extras first and lays the modelled fields on top.
//
// ── ⚠ THE MIGRATION'S HEADER IS STALE AND SAYS THE OPPOSITE ──
//
// It reads *"INERT: nothing in the app reads these until the Practice rework"*.
// That stopped being true when beta's tab shipped — `renderPractice` is 295 lines
// at app/index.html:28699 and is registered at :28992. Believing the header cost
// this port a filed issue that claimed there was nothing to relocate.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { reportFailureAs } from '../diagnostics/errorLog';
import { PAGE_ROWS } from './load';

/**
 * One import of selected trades.
 *
 * ⚠ `marketDate` is a derived LABEL, not an identity — the plan is explicit that
 * a session is one import, and its date is what that import happened to cover.
 */
export interface PracticeSession {
  id: string;
  /** 'YYYY-MM-DD' or '' — a DATE column that can be null. */
  marketDate: string;
  label: string;
  /** De-cluttered, not deleted. The tab can show these again. */
  dismissed: boolean;
  importedAt: string | null;
  /**
   * 🚨 THE ORIGINAL BLOB, CARRIED. Session grade, mode, the mirror link and the
   * delta scorecard all ride here until real use earns them a column. A save
   * spreads this and lays known fields over it.
   */
  extras: Record<string, unknown>;
}

/**
 * One copied trade inside a session.
 *
 * ⚠ `trade` is the WHOLE trade object as the app models it — not a subset. The
 * flat columns below are the queryable projection of it, and the projection is
 * rebuilt from the object on save rather than edited alongside it, so the two
 * cannot disagree.
 */
export interface PracticeTrade {
  id: string;
  sessionId: string;
  /** The live trade this was copied from. Empty when unknown. */
  sourceTradeId: string;
  /** 🚨 Everything. See the header — this is carried, never rebuilt. */
  trade: Record<string, unknown>;
}

// ── mapping ──────────────────────────────────────────────────────────────────

export function practiceSessionFromDb(r: Record<string, unknown>): PracticeSession {
  const extras = (r['extras'] ?? {}) as Record<string, unknown>;
  return {
    id: String(r['id'] ?? ''),
    marketDate: (r['market_date'] as string | null) ?? '',
    label: (r['label'] as string | null) ?? '',
    dismissed: r['dismissed'] === true,
    importedAt: (r['imported_at'] as string | null) ?? null,
    extras,
  };
}

/**
 * ⚠ THE BLOB FIRST, THE COLUMNS OVER IT — the old client's own order
 * (`...(r.extras || {})` last would let a stale extras key shadow a real column,
 * so the id and the session link are applied afterwards).
 */
export function practiceTradeFromDb(r: Record<string, unknown>): PracticeTrade {
  const extras = (r['extras'] ?? {}) as Record<string, unknown>;
  return {
    id: String(r['id'] ?? ''),
    sessionId: String(r['session_id'] ?? ''),
    sourceTradeId: (r['source_trade_id'] as string | null) ?? '',
    trade: { ...extras, id: String(r['id'] ?? '') },
  };
}

const num = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function practiceSessionToRow(
  s: PracticeSession, userId: string,
): Record<string, unknown> {
  return {
    id: s.id,
    user_id: userId,
    // ⚠ NULL, not '', for an empty date — the column is DATE and Postgres
    // rejects an empty string with a type error rather than storing a blank.
    market_date: s.marketDate || null,
    label: s.label,
    dismissed: s.dismissed,
    imported_at: s.importedAt,
    // 🚨 CARRIED. See the header.
    extras: s.extras,
  };
}

/**
 * ⚠ The flat columns are DERIVED from the trade object on every write, so the
 * projection cannot drift from the thing it projects. Editing them separately is
 * how two copies of one number end up disagreeing on the same screen.
 */
export function practiceTradeToRow(
  p: PracticeTrade, userId: string,
): Record<string, unknown> {
  const t = p.trade;
  return {
    id: p.id,
    user_id: userId,
    session_id: p.sessionId,
    source_trade_id: p.sourceTradeId,
    date: (t['date'] as string) || null,
    side: (t['side'] as string) ?? '',
    contract: (t['contract'] as string) ?? '',
    entry: num(t['entry']),
    exit: num(t['exit']),
    qty: num(t['qty']),
    net_pnl: num(t['netPnl']),
    mae_points: num(t['maePoints']),
    mfe_points: num(t['mfePoints']),
    pattern_id: (t['patternId'] as string) ?? '',
    exit_model_id: (t['exitModelId'] as string) ?? '',
    grade: (t['grade'] as string) ?? '',
    notes: (t['notes'] as string) ?? '',
    screenshots: Array.isArray(t['screenshots']) ? t['screenshots'] : [],
    // 🚨 THE WHOLE OBJECT. Not a subset, not a rebuild.
    extras: t,
  };
}

// ── reads ────────────────────────────────────────────────────────────────────

/**
 * 🚨 PAGES TO EXHAUSTION. `.limit(n)` DOES NOT DO WHAT IT LOOKS LIKE — PostgREST
 * caps a response at its configured maximum (1,000 here) whatever you ask for,
 * so a single request silently returns the first page and nothing says so.
 *
 * ⚠ MEASURED, not theorised: a one-shot count of `trades` during this port
 * reported **3 distinct users** when the real number is **16**. It read one page
 * of 4,606 rows and the answer looked entirely plausible.
 *
 * `loadTradeMetrics` in this same directory already pages for exactly this
 * reason, and its comment is worth reading. A practice book of more than a
 * thousand copied trades is ordinary after a few months of reps.
 */
async function readAll<T>(
  sb: SupabaseClient,
  table: string,
  select: string,
  map: (r: Record<string, unknown>) => T,
  order?: { column: string; ascending: boolean },
): Promise<T[]> {
  const out: T[] = [];
  for (let from = 0; ; from += PAGE_ROWS) {
    let q = sb.from(table).select(select);
    if (order) q = q.order(order.column, { ascending: order.ascending });
    // ⚠ A stable tiebreak, so a row cannot appear on two pages or on neither
    // when the ordering column has duplicates — every session on one date would.
    const { data, error } = await q.order('id', { ascending: true })
      .range(from, from + PAGE_ROWS - 1);
    // Fail LOUD. A partial practice book renders as a session that has quietly
    // lost trades, which looks exactly like a session that never had them.
    if (error) throw new Error(`${table} read failed: ${error.message}`);
    for (const r of data ?? []) out.push(map(r as unknown as Record<string, unknown>));
    if (!data || data.length < PAGE_ROWS) return out;
  }
}

export function loadPracticeSessions(sb: SupabaseClient): Promise<PracticeSession[]> {
  return readAll(
    sb, 'practice_sessions', 'id,market_date,label,dismissed,imported_at,extras',
    practiceSessionFromDb, { column: 'market_date', ascending: false },
  );
}

export function loadPracticeTrades(sb: SupabaseClient): Promise<PracticeTrade[]> {
  return readAll(
    sb, 'practice_trades', 'id,session_id,source_trade_id,extras', practiceTradeFromDb,
  );
}

// ── writes ───────────────────────────────────────────────────────────────────

export interface PracticeWriteResult { error: string | null }

/**
 * Import a set of copied trades as ONE new session.
 *
 * 🚨 THE SESSION GOES FIRST, AND ITS FAILURE STOPS EVERYTHING. `practice_trades`
 * has a foreign key onto `practice_sessions` with ON DELETE CASCADE, so trades
 * written against a session row that does not exist are rejected — and writing
 * them first would leave the ones that slipped through orphaned and invisible.
 *
 * ⚠ NOT a transaction. PostgREST has no cross-table one here, so a failure part
 * way through leaves a session with fewer trades than intended — recoverable, and
 * reported, where a silent partial import would not be.
 */
export async function importPracticeSession(
  sb: SupabaseClient,
  userId: string,
  session: PracticeSession,
  trades: readonly PracticeTrade[],
): Promise<PracticeWriteResult> {
  const { error: sErr } = await sb
    .from('practice_sessions')
    .insert(practiceSessionToRow(session, userId));
  if (sErr) {
    return {
      error: reportFailureAs('import practice session', sErr,
        // ⚠ The unique constraint is (user_id, market_date, run_token), so this
        // is the "you already imported this replay" case and the message says so
        // rather than showing a constraint name.
        /duplicate key|unique/i.test(sErr.message)
          ? 'That session is already imported. Re-importing the same replay would duplicate it.'
          : 'The session could not be created, so nothing was imported.'),
    };
  }

  if (!trades.length) return { error: null };

  const { error: tErr } = await sb
    .from('practice_trades')
    .insert(trades.map((t) => practiceTradeToRow(t, userId)));
  if (tErr) {
    return {
      error: reportFailureAs('import practice trades', tErr,
        'The session was created but its trades were not — open it and import again.'),
    };
  }
  return { error: null };
}

/** Update a session's own fields — grade, notes, label, dismissed. */
export async function savePracticeSession(
  sb: SupabaseClient, userId: string, session: PracticeSession,
): Promise<PracticeWriteResult> {
  // ⚠ UPDATE, never upsert. An upsert on a mistyped id invents a phantom
  // session; an update matching nothing is detectable.
  const { error } = await sb
    .from('practice_sessions')
    .update(practiceSessionToRow(session, userId))
    .eq('id', session.id)
    .eq('user_id', userId);
  if (error) {
    return { error: reportFailureAs('save practice session', error, 'That was not saved.') };
  }
  return { error: null };
}

/** Persist an annotation made on one copied trade. */
export async function savePracticeTrade(
  sb: SupabaseClient, userId: string, trade: PracticeTrade,
): Promise<PracticeWriteResult> {
  const { error } = await sb
    .from('practice_trades')
    .update(practiceTradeToRow(trade, userId))
    .eq('id', trade.id)
    .eq('user_id', userId);
  if (error) {
    return { error: reportFailureAs('save practice trade', error, 'That was not saved.') };
  }
  return { error: null };
}

/**
 * Delete a session and, by cascade, its trades.
 *
 * ⚠ THE CASCADE IS THE POINT AND IT IS ALSO THE HAZARD. `practice_trades` is
 * `ON DELETE CASCADE`, so this discards every annotation made inside the
 * session. The caller must say so before asking.
 */
export async function deletePracticeSession(
  sb: SupabaseClient, userId: string, id: string,
): Promise<PracticeWriteResult> {
  const { error } = await sb
    .from('practice_sessions').delete().eq('id', id).eq('user_id', userId);
  if (error) {
    return { error: reportFailureAs('delete practice session', error, 'That was not deleted.') };
  }
  return { error: null };
}
